import { useEffect, useState } from "react";

import { postAdmin } from "../lib/api";
import { LoadingState } from "../components/LoadingState";

interface AdminSnapshot {
  environment: string;
  state: {
    currentVersion: number;
    currentPriceCents: number;
    successfulPressCount: number;
    grossCapturedSubtotalCents: number;
    netCapturedSubtotalCents: number;
    refundedSubtotalCents: number;
    paymentsPaused: boolean;
    maintenanceMode: boolean;
    captureClaimAttemptId: string | null;
  };
  attemptCounts: Record<string, number>;
  webhookCounts: Record<string, number>;
  recentActions: Array<Record<string, unknown>>;
}

async function loadAdmin(): Promise<AdminSnapshot> {
  const response = await fetch("/api/admin/state", { cache: "no-store" });
  if (!response.ok)
    throw new Error(
      response.status === 401 ? "Cloudflare Access is required." : "Admin state is unavailable.",
    );
  return response.json<AdminSnapshot>();
}

export function AdminPage() {
  const [snapshot, setSnapshot] = useState<AdminSnapshot | null>(null);
  const [reason, setReason] = useState("");
  const [pressNumber, setPressNumber] = useState("");
  const [moderationAction, setModerationAction] = useState("hide-text");
  const [resetReason, setResetReason] = useState("");
  const [resetConfirmation, setResetConfirmation] = useState("");
  const [resetAcknowledged, setResetAcknowledged] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = () =>
    loadAdmin()
      .then((data) => {
        setSnapshot(data);
        setError(null);
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Admin unavailable."),
      );

  useEffect(() => {
    void refresh();
  }, []);

  const act = (path: string) => {
    if (reason.trim().length < 8) {
      setError("Enter an audit reason of at least 8 characters.");
      return;
    }
    setBusy(true);
    setNotice(null);
    void postAdmin(path, { reason })
      .then(() => {
        setReason("");
        return refresh();
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Action failed."),
      )
      .finally(() => setBusy(false));
  };

  const resetStaging = () => {
    if (resetReason.trim().length < 8 || resetConfirmation !== "RESET STAGING") {
      setError("Type RESET STAGING and enter an audit reason of at least 8 characters.");
      return;
    }
    if (!resetAcknowledged) {
      setError("Confirm that Stripe sandbox records remain outside this app reset.");
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    void postAdmin("staging/reset", {
      confirmation: resetConfirmation,
      reason: resetReason,
    })
      .then(() => {
        setResetReason("");
        setResetConfirmation("");
        setResetAcknowledged(false);
        setNotice("Staging reset complete. The next simulated purchase will be Press #1 at $1.");
        return refresh();
      })
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : "Staging reset failed."),
      )
      .finally(() => setBusy(false));
  };

  if (!snapshot) {
    return (
      <main className="centered-page">
        {error ? <h1>{error}</h1> : <LoadingState label="Reading protected operations" />}
      </main>
    );
  }

  return (
    <main className="content-page admin-page">
      <header className="page-intro">
        <p className="section-kicker">PROTECTED OPERATIONS</p>
        <h1>ADMINISTRATION</h1>
        <p>Environment: {snapshot.environment.toUpperCase()}</p>
      </header>

      <dl className="admin-metrics">
        <div>
          <dt>VERSION</dt>
          <dd>{snapshot.state.currentVersion}</dd>
        </div>
        <div>
          <dt>MINIMUM CENTS</dt>
          <dd>{snapshot.state.currentPriceCents}</dd>
        </div>
        <div>
          <dt>PRESSES</dt>
          <dd>{snapshot.state.successfulPressCount}</dd>
        </div>
        <div>
          <dt>PAYMENTS</dt>
          <dd>{snapshot.state.paymentsPaused ? "PAUSED" : "OPEN"}</dd>
        </div>
        <div>
          <dt>MAINTENANCE</dt>
          <dd>{snapshot.state.maintenanceMode ? "ON" : "OFF"}</dd>
        </div>
        <div>
          <dt>ACTIVE CLAIM</dt>
          <dd>{snapshot.state.captureClaimAttemptId ? "YES" : "NO"}</dd>
        </div>
      </dl>

      <section className="admin-controls">
        <h2>Operational controls</h2>
        <label className="field">
          <span>Audit reason</span>
          <textarea
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            rows={3}
            maxLength={500}
          />
        </label>
        <div className="admin-action-row">
          <button type="button" disabled={busy} onClick={() => act("pause")}>
            PAUSE PAYMENTS
          </button>
          <button type="button" disabled={busy} onClick={() => act("unpause")}>
            RESUME PAYMENTS
          </button>
          <button type="button" disabled={busy} onClick={() => act("maintenance/enable")}>
            ENABLE MAINTENANCE
          </button>
          <button type="button" disabled={busy} onClick={() => act("maintenance/disable")}>
            DISABLE MAINTENANCE
          </button>
        </div>
      </section>

      {snapshot.environment === "staging" ? (
        <section className="admin-controls admin-danger-zone" aria-labelledby="staging-reset-title">
          <p className="section-kicker">STAGING ONLY · DESTRUCTIVE</p>
          <h2 id="staging-reset-title">Reset simulated purchases</h2>
          <p>
            Clears staging presses, checkout attempts, holder history, counters and rate limits,
            then restores Press #1 at a $1 minimum. Production cannot call this endpoint. Audit
            records and Stripe sandbox transactions remain.
          </p>
          <div className="admin-inline-fields">
            <label className="field">
              <span>Audit reason</span>
              <textarea
                value={resetReason}
                onChange={(event) => setResetReason(event.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Why is staging being reset?"
              />
            </label>
            <label className="field">
              <span>Type RESET STAGING</span>
              <input
                value={resetConfirmation}
                onChange={(event) => setResetConfirmation(event.target.value)}
                autoComplete="off"
                spellCheck={false}
              />
            </label>
          </div>
          <label className="admin-reset-acknowledgement">
            <input
              type="checkbox"
              checked={resetAcknowledged}
              onChange={(event) => setResetAcknowledged(event.target.checked)}
            />
            <span>
              I understand this clears only the app state. Stripe test transactions remain in the
              sandbox for debugging.
            </span>
          </label>
          <button
            className="danger-button"
            type="button"
            disabled={
              busy ||
              !resetAcknowledged ||
              resetConfirmation !== "RESET STAGING" ||
              resetReason.trim().length < 8
            }
            onClick={resetStaging}
          >
            RESET STAGING TO $1
          </button>
        </section>
      ) : null}

      <section className="admin-controls">
        <h2>Moderation</h2>
        <div className="admin-inline-fields">
          <label className="field">
            <span>Press number</span>
            <input
              type="number"
              min="1"
              step="1"
              value={pressNumber}
              onChange={(event) => setPressNumber(event.target.value)}
            />
          </label>
          <label className="field">
            <span>Action</span>
            <select
              value={moderationAction}
              onChange={(event) => setModerationAction(event.target.value)}
            >
              <option value="hide-text">Hide text</option>
              <option value="restore-text">Restore text</option>
              <option value="disable-link">Disable link</option>
              <option value="restore-link">Restore link</option>
              <option value="disqualify">Disqualify</option>
              <option value="restore-eligibility">Restore eligibility</option>
            </select>
          </label>
        </div>
        <button
          type="button"
          disabled={busy || !pressNumber}
          onClick={() => act(`press/${pressNumber}/${moderationAction}`)}
        >
          APPLY MODERATION
        </button>
      </section>

      <section className="admin-status-grid">
        <div>
          <h2>Attempts</h2>
          <pre>{JSON.stringify(snapshot.attemptCounts, null, 2)}</pre>
        </div>
        <div>
          <h2>Webhooks</h2>
          <pre>{JSON.stringify(snapshot.webhookCounts, null, 2)}</pre>
        </div>
      </section>
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="admin-notice" role="status">
          {notice}
        </p>
      ) : null}
    </main>
  );
}
